import React, { useEffect } from 'react';
import Plx from 'react-plx';
import { makeStyles } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/core/styles';
import { yellow } from '@material-ui/core/colors';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import RadialBarChart from 'components/RadialBarChart';
import Astrum from 'components/Astrum';
import useWindowPositioning from 'hooks/useWindowPositioning';
import InsurerDetails from './InsurerDetails';
import './index.css';

const DayNightSwitch = withStyles((theme) => ({
  root: {
    width: 62,
    height: 34,
    padding: '8px 12px 8px 8px',
  },
  switchBase: {
    color: yellow[300],
    padding: 1,
    '&$checked + $track': {
      backgroundColor: yellow[500],
    },
    '&$checked .MuiSwitch-thumb': {
      backgroundColor: yellow[700],
      '&:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
  },
  thumb: {
    width: 32,
    height: 32,
    marginLeft: 1,
    backgroundColor: '#fff',
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#001e3c',
      )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
    },
  },
  checked: {},
  track: {
    borderRadius: 20 / 2,
  },
}))(Switch);

const useStyles = makeStyles((theme) => ({
  foliageRight: {
    transform: 'translateX(50%) scale(1)',
  },
  foliageLeft: {
    transform: 'translateX(-20%) scale(1)',
  },
  neonSign: {
    opacity: 0,
  },
  dataWrapper: {
    display: 'flex',
    width: '100%',
    marginTop: '5rem',
  },
  chart: {
    flex: 1,
    textAlign: 'center',
  },
  details: {
    flex: 1,
    marginTop: '3.75rem',
    paddingRight: '1rem',
  },
  switchWrapper: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.5rem',
    borderRadius: '1.625rem',
    transition: 'all 0.3s ease-in-out',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  switchLabelLeft: {
    flex: 1,
    textAlign: 'right',
    paddingRight: '0.5rem',
    paddingLeft: '1rem',
  },
  switch: {},
  switchLabelRight: {
    flexBasis: 1,
    textAlign: 'left',
    paddingRight: '0.5rem',
    paddingLeft: '0.5rem',
  },
  sunMoon: {
    display: 'block',
    backgroundColor: (props) => (props.isDay ? '#f9d71c' : '#eee'),
    boxShadow: (props) => (props.isDay ? '0px 0px 50px 9px #f9d71c' : '0px 0px 50px 15px #FFFFFF'),
    height: '100px',
    width: '100px',
    borderRadius: '50%',
    position: 'absolute',
    top: '0',
    right: '20%',
  },
  title: {
    position: 'absolute',
    top: '4rem',
    left: '0',
    width: '100%',
    textAlign: 'center',
    color: '#fff',
    fontSize: '30px',
    zIndex: 10000,
    fontFamily: 'Roboto Slab',
    fontWeight: '700',
    textShadow: '0px 0px 25px #FFFFFF',
  },
}));

const HeroTopInsurers = ({ data: { allLines = [], commercialLines = [] } = {}, year, quarter }) => {
  const { windowDimensions, elementRef } = useWindowPositioning();
  const [state, setState] = React.useState({
    isDay: false,
    selectedItem: {},
    color: '',
  });
  const [data, setData] = React.useState([]);
  const classes = useStyles({ ...state });
  const panelSize = '600px';

  const Title = ({ isDay, addQuarter }) =>
    isDay
      ? addQuarter
        ? `Top Insurers Q${quarter} ${year} - Commercial Lines`
        : 'Top Insurers - Commercial Lines'
      : addQuarter
      ? `Top Insurers Q${quarter} ${year} - All Lines`
      : 'Top Insurers - All Lines';

  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.checked });

    if (event.target.checked) {
      setData(commercialLines);
    } else {
      setData(allLines);
    }
  };

  const handleItemClick = (item, color) => {
    setState({
      ...state,
      selectedItem: item,
      color,
    });
  };

  useEffect(() => {
    setData(allLines);
  }, [allLines]);

  const { isDay, selectedItem } = state;

  return (
    <>
      <div ref={elementRef} style={{ height: panelSize, position: 'relative', overflow: 'hidden' }}>
        {/* Right Foliage */}
        <Plx
          data-testid="foliage-right-panel"
          className={classes.foliageRight}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd,
              easing: 'ease-in',
              properties: [
                {
                  startValue: 1,
                  endValue: 1.8,
                  property: 'scale',
                },
                {
                  startValue: 50,
                  endValue: 100,
                  property: 'translateX',
                  unit: '%',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 1.8,
                  endValue: 1,
                  property: 'scale',
                },
                {
                  startValue: 100,
                  endValue: 50,
                  property: 'translateX',
                  unit: '%',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            right: 0,
            width: '100%',
            height: panelSize,
            zIndex: 100,
            backgroundImage: 'url(trees.png)',
            ...(isDay && { filter: 'saturate(4) brightness(1.75)' }),
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1200px',
            backgroundPosition: 'left center',
          }}
        />
        {/* Left Foliage */}
        <Plx
          data-testid="foliage-left-panel"
          className={classes.foliageLeft}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd,
              easing: 'ease-in',
              properties: [
                {
                  startValue: 1,
                  endValue: 1.8,
                  property: 'scale',
                },
                {
                  startValue: -30,
                  endValue: -70,
                  property: 'translateX',
                  unit: '%',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 1.8,
                  endValue: 1,
                  property: 'scale',
                },
                {
                  startValue: -70,
                  endValue: -30,
                  property: 'translateX',
                  unit: '%',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            right: 0,
            width: '140%',
            height: panelSize,
            zIndex: 100,
            backgroundImage: 'url(trees.png)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '1200px',
            backgroundPosition: 'center center',
          }}
        />
        {/* Sky Background */}
        <Plx
          data-testid="sky-background-panel"
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd,
              properties: [
                {
                  startValue: 1,
                  endValue: 1.18,
                  property: 'scale',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 1.18,
                  endValue: 1,
                  property: 'opacity',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: panelSize,
            background:
              'linear-gradient(to right, rgb(36, 36, 62), rgb(48, 43, 99), rgb(15, 12, 41))',
            ...(isDay && { background: 'linear-gradient(to bottom, #57c1eb 0%,#246fa8 100%)' }),
          }}
        >
          <div className={classes.sunMoon}></div>
          <Typography className={`${classes.title} insurer-chart-title-under`}>
            <Title isDay={isDay} />
            <br />Q{quarter} {year}
          </Typography>

          {!isDay && <Astrum height="550" width="1500" speed={0.2} />}
        </Plx>
        {/* Chart */}
        <Plx
          data-testid="data-panel"
          className={classes.neonSign}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd,
              properties: [
                {
                  startValue: 0,
                  endValue: 1,
                  property: 'opacity',
                },
              ],
            },
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd,
              properties: [
                {
                  startValue: 0.8,
                  endValue: 1,
                  property: 'scale',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 1,
                  endValue: 0,
                  property: 'opacity',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 1,
                  endValue: 0.8,
                  property: 'scale',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            width: '100%',
            height: panelSize,
            zIndex: 200,
            paddingTop: '1rem',
          }}
        >
          {data.length > 0 ? (
            <div className={classes.dataWrapper}>
              <div className={`${classes.chart} insurer-chart-wrapper`} id="TopInsurers">
                <RadialBarChart
                  day={isDay}
                  data={data}
                  onItemClick={handleItemClick}
                  valueKey="totalPremium"
                />
                <div className={classes.switchWrapper}>
                  <div className={classes.switchLabelLeft}>All Lines</div>
                  <div className={classes.switch}>
                    <DayNightSwitch checked={isDay} onChange={handleChange} name="isDay" />
                  </div>
                  <div className={classes.switchLabelRight}>Commercial</div>
                </div>
              </div>
              <div className={`${classes.details} insurer-chart-details`}>
                <Typography className={`${classes.title} insurer-chart-title-over`}>
                  <Title isDay={isDay} addQuarter={true} />
                </Typography>
                <InsurerDetails
                  insurer={selectedItem}
                  day={isDay}
                  color={state.color}
                  year={year}
                  quarter={quarter}
                />
              </div>
            </div>
          ) : null}
        </Plx>
      </div>
    </>
  );
};

export default HeroTopInsurers;
